/**
 * The version each open rules tab was loaded from (or last saved as). Sent as `base_version`
 * on save, so a write over someone else's newer version is a 409, not a silent overwrite.
 * Deliberately NOT read from the query cache: a background refetch would move the base under
 * unsaved edits and hide the conflict.
 */
import { create } from 'zustand';
import { registerOrgScopedReset } from '@/lib/org-scope';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import type { RulesScope } from '@/services/rules/types';

interface RulesBaseVersionState {
  /** `${orgId}:${scope}` -> version. */
  versions: Record<string, number>;
  setBase: (orgId: string, scope: RulesScope, version: number) => void;
  getBase: (orgId: string, scope: RulesScope) => number | undefined;
}

export const rulesBaseKey = (orgId: string, scope: RulesScope) => `${orgId}:${scope}`;

export const useRulesBaseVersionStore = create<RulesBaseVersionState>((set, get) => ({
  versions: {},
  setBase: (orgId, scope, version) =>
    set((s) => ({ versions: { ...s.versions, [rulesBaseKey(orgId, scope)]: version } })),
  getBase: (orgId, scope) => get().versions[rulesBaseKey(orgId, scope)],
}));

// Sign-out / org change: a base version must not outlive the session that loaded it.
registerOrgScopedReset('rulesBaseVersionStore', () => useRulesBaseVersionStore.setState({ versions: {} }));
useAuthStoreV1.subscribe((state, prev) => {
  if (prev.user && !state.user) useRulesBaseVersionStore.setState({ versions: {} });
});
